/**
 * Print-preview camera binder extracted from `Scene3DStage.tsx`.
 *
 * Pure code move — no behaviour change. The component was lifted out
 * verbatim to keep `Scene3DStage.tsx` under the repo's hard 2000-line
 * ceiling (eslint `max-lines` + the "Source hygiene" CI check) after
 * `main` independently grew the file.
 *
 * The props type is derived from the `Scene3DStageProps` **contract**
 * module rather than from `Scene3DStage.tsx` (which merely re-exports
 * an alias of it) — importing the stage here would close an import
 * cycle, and the repo enforces a global zero-cycle dependency gate.
 */

import * as React from "react";
import { useEffect } from "react";

import type { Scene3DStageProps } from "../Scene3DStageProps";
import type { OverlayState } from "../overlays";
import { useCameraSnapshotVersion } from "../grips/gripsOverlay";
import { applyPrintPreview } from "./StageOverlayState";

/**
 * Codex P2 follow-up — gate the camera-version subscription behind
 * the print-preview dialog state so it only fires while the dialog is
 * actually open. Subscribing always (Phase 1.h pattern) risked
 * useSyncExternalStore tearing during bulk-load; subscribing never
 * (Phase 1.k revert) froze the preview rect on pan/zoom. Mounting
 * this binder only when `bounds !== null` is the safe middle ground.
 */
export function PrintPreviewCameraBinder({
  bounds,
  setOverlayState,
}: {
  bounds: NonNullable<Scene3DStageProps["printPreviewWorldBounds"]>;
  setOverlayState: React.Dispatch<React.SetStateAction<OverlayState>>;
}): null {
  const cameraVersion = useCameraSnapshotVersion();
  useEffect(() => {
    applyPrintPreview(setOverlayState, bounds);
  }, [bounds, cameraVersion, setOverlayState]);
  return null;
}
